import type { DashboardExerciseType } from '../../types/dashboard'

// ---------------------------------------------------------------------------
// App Open
// ---------------------------------------------------------------------------

export interface AppOpenProperties {
  [key: string]: unknown
  platform: string
  app_version: string
  build_number: string
  locale: string
  flavour: string
  environment: string
  session_number?: number
  days_since_install?: number
}

export function buildAppOpenProperties(params: {
  platform: string
  appVersion: string
  buildNumber: string
  locale: string
  flavour: string
  environment: string
  sessionNumber?: number
  daysSinceInstall?: number
}): AppOpenProperties {
  const props: AppOpenProperties = {
    platform: params.platform,
    app_version: params.appVersion,
    build_number: params.buildNumber,
    locale: params.locale,
    flavour: params.flavour,
    environment: params.environment,
  }
  if (params.sessionNumber != null) props.session_number = params.sessionNumber
  if (params.daysSinceInstall != null)
    props.days_since_install = params.daysSinceInstall
  return props
}

// ---------------------------------------------------------------------------
// Session / Course Lifecycle
// ---------------------------------------------------------------------------

export interface SessionLifecycleProperties {
  [key: string]: unknown
  id: string
  name: string
  course_id?: string
  course_name?: string
  course_category?: string
  weekId?: string
  subjectId?: string
  programId?: string
  exerciseType?: DashboardExerciseType
  total_duration_s?: number
}

export function buildSessionLifecycleProperties(params: {
  sessionId: string
  sessionName: string
  courseCategory?: string
  weekId?: string
  subjectId?: string
  programId?: string
  exerciseType?: DashboardExerciseType
  totalDurationS?: number
}): SessionLifecycleProperties {
  const props: SessionLifecycleProperties = {
    id: params.sessionId,
    name: params.sessionName,
    course_id: params.sessionId,
    course_name: params.sessionName,
  }
  if (params.courseCategory != null)
    props.course_category = params.courseCategory
  if (params.weekId != null) props.weekId = params.weekId
  if (params.subjectId != null) props.subjectId = params.subjectId
  if (params.programId != null) props.programId = params.programId
  if (params.exerciseType != null) props.exerciseType = params.exerciseType
  if (params.totalDurationS != null)
    props.total_duration_s = params.totalDurationS
  return props
}

// ---------------------------------------------------------------------------
// Step Lifecycle
// ---------------------------------------------------------------------------

export interface StepLifecycleProperties {
  [key: string]: unknown
  courseid: string
  stepid: string
  courseName: string
  stepName: string
  step_number?: number
  step_type?: string
  duration_s?: number
  completion_pct?: number
}

export function buildStepLifecycleProperties(params: {
  sessionId: string
  stepId: string
  sessionName: string
  stepName: string
  stepNumber?: number
  stepType?: string
  durationS?: number
  completionPct?: number
}): StepLifecycleProperties {
  const props: StepLifecycleProperties = {
    courseid: params.sessionId,
    stepid: params.stepId,
    courseName: params.sessionName,
    stepName: params.stepName,
  }
  if (params.stepNumber != null) props.step_number = params.stepNumber
  if (params.stepType != null) props.step_type = params.stepType
  if (params.durationS != null) props.duration_s = params.durationS
  if (params.completionPct != null) props.completion_pct = params.completionPct
  return props
}

// ---------------------------------------------------------------------------
// Favourites
// ---------------------------------------------------------------------------

export interface FavoriteProperties {
  [key: string]: unknown
  id: string
  name: string
}

export function buildFavoriteProperties(params: {
  contentfulId: string
  sessionName: string
}): FavoriteProperties {
  return {
    id: params.contentfulId,
    name: params.sessionName,
  }
}

// ---------------------------------------------------------------------------
// Voice Assessment
// ---------------------------------------------------------------------------

type VoiceAssessmentVoiceType = string | null

interface VoiceAssessmentBaseProperties {
  [key: string]: unknown
  session_id: number
  session_definition_id: number
  voice_type: VoiceAssessmentVoiceType
}

interface VoiceAssessmentMeasurementBaseProperties extends VoiceAssessmentBaseProperties {
  measurement_id: number | null
  measurement_definition_id: number
  measurement_index: number
}

export interface VoiceAssessmentStartedProperties extends VoiceAssessmentBaseProperties {
  total_measurements: number
}

export function buildVoiceAssessmentStartedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  totalMeasurements: number
}): VoiceAssessmentStartedProperties {
  return {
    session_id: params.sessionId,
    session_definition_id: params.sessionDefinitionId,
    voice_type: params.voiceType,
    total_measurements: params.totalMeasurements,
  }
}

export interface VoiceAssessmentResumedProperties extends VoiceAssessmentStartedProperties {
  completed_measurements: number
  resume_measurement_index: number
}

export function buildVoiceAssessmentResumedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  totalMeasurements: number
  completedMeasurements: number
  resumeMeasurementIndex: number
}): VoiceAssessmentResumedProperties {
  return {
    ...buildVoiceAssessmentStartedProperties(params),
    completed_measurements: params.completedMeasurements,
    resume_measurement_index: params.resumeMeasurementIndex,
  }
}

export interface VoiceAssessmentMeasurementRecordingCompletedProperties extends VoiceAssessmentMeasurementBaseProperties {
  has_questions: boolean
  has_recording: boolean
}

export function buildVoiceAssessmentMeasurementRecordingCompletedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  measurementId: number | null
  measurementDefinitionId: number
  measurementIndex: number
  hasQuestions: boolean
  hasRecording: boolean
}): VoiceAssessmentMeasurementRecordingCompletedProperties {
  return {
    session_id: params.sessionId,
    session_definition_id: params.sessionDefinitionId,
    voice_type: params.voiceType,
    measurement_id: params.measurementId,
    measurement_definition_id: params.measurementDefinitionId,
    measurement_index: params.measurementIndex,
    has_questions: params.hasQuestions,
    has_recording: params.hasRecording,
  }
}

export interface VoiceAssessmentMeasurementQuestionsCompletedProperties extends VoiceAssessmentMeasurementBaseProperties {
  question_count: number
  answered_question_count: number
}

export function buildVoiceAssessmentMeasurementQuestionsCompletedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  measurementId: number | null
  measurementDefinitionId: number
  measurementIndex: number
  questionCount: number
  answeredQuestionCount: number
}): VoiceAssessmentMeasurementQuestionsCompletedProperties {
  return {
    session_id: params.sessionId,
    session_definition_id: params.sessionDefinitionId,
    voice_type: params.voiceType,
    measurement_id: params.measurementId,
    measurement_definition_id: params.measurementDefinitionId,
    measurement_index: params.measurementIndex,
    question_count: params.questionCount,
    answered_question_count: params.answeredQuestionCount,
  }
}

export interface VoiceAssessmentMeasurementSubmittedProperties extends VoiceAssessmentMeasurementQuestionsCompletedProperties {
  measurement_id: number
  has_recording: boolean
}

export function buildVoiceAssessmentMeasurementSubmittedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  measurementId: number
  measurementDefinitionId: number
  measurementIndex: number
  hasRecording: boolean
  questionCount: number
  answeredQuestionCount: number
}): VoiceAssessmentMeasurementSubmittedProperties {
  return {
    ...buildVoiceAssessmentMeasurementQuestionsCompletedProperties(params),
    measurement_id: params.measurementId,
    has_recording: params.hasRecording,
  }
}

export interface VoiceAssessmentCompletedProperties extends VoiceAssessmentBaseProperties {
  has_reflection: boolean
  reflection_length: number
}

export function buildVoiceAssessmentCompletedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  hasReflection: boolean
  reflectionLength: number
}): VoiceAssessmentCompletedProperties {
  return {
    session_id: params.sessionId,
    session_definition_id: params.sessionDefinitionId,
    voice_type: params.voiceType,
    has_reflection: params.hasReflection,
    reflection_length: params.reflectionLength,
  }
}

export interface VoiceAssessmentAbandonedProperties extends VoiceAssessmentCompletedProperties {
  measurement_index: number
  completed_measurements: number
}

export function buildVoiceAssessmentAbandonedProperties(params: {
  sessionId: number
  sessionDefinitionId: number
  voiceType: VoiceAssessmentVoiceType
  measurementIndex: number
  completedMeasurements: number
  hasReflection: boolean
  reflectionLength: number
}): VoiceAssessmentAbandonedProperties {
  return {
    ...buildVoiceAssessmentCompletedProperties(params),
    measurement_index: params.measurementIndex,
    completed_measurements: params.completedMeasurements,
  }
}

// ---------------------------------------------------------------------------
// Purchase / Subscription
// ---------------------------------------------------------------------------

export interface PurchaseProperties {
  [key: string]: unknown
  user_id: string | null
  product_id: string
  store: string
  price: number
  currency: string
  plan_type: string
  is_trial: boolean
}

export function buildPurchaseProperties(params: {
  userId?: string | null
  productId: string
  store: string
  price: number
  currency: string
  planType: string
  isTrial: boolean
}): PurchaseProperties {
  return {
    user_id: params.userId ?? null,
    product_id: params.productId,
    store: params.store,
    price: params.price,
    currency: params.currency,
    plan_type: params.planType,
    is_trial: params.isTrial,
  }
}

// ---------------------------------------------------------------------------
// Signup
// ---------------------------------------------------------------------------

export interface SignupProperties {
  [key: string]: unknown
  email: string | null
}

export function buildSignupProperties(params: {
  email?: string | null
}): SignupProperties {
  return {
    email: params.email ?? null,
  }
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

export interface AttributionProperties {
  platform: string
  attribution_source: string
  timestamp: string
  store_type?: string
  readable_store?: string
  [key: string]: string | undefined
}

export function buildAttributionProperties(params: {
  platform: string
  attributionSource: string
  storeType?: string
  readableStore?: string
  utmParams?: Record<string, string>
}): AttributionProperties {
  const props: AttributionProperties = {
    platform: params.platform,
    attribution_source: params.attributionSource,
    timestamp: new Date().toISOString(),
  }
  if (params.storeType != null) props.store_type = params.storeType
  if (params.readableStore != null) props.readable_store = params.readableStore
  if (params.utmParams != null) {
    Object.entries(params.utmParams).forEach(([key, value]) => {
      props[key] = value
    })
  }
  return props
}

// ---------------------------------------------------------------------------
// Onboarding (P0 — new events)
// ---------------------------------------------------------------------------

export interface OnboardingStartedProperties {
  [key: string]: unknown
  total_steps: number
}

export function buildOnboardingStartedProperties(params: {
  totalSteps: number
}): OnboardingStartedProperties {
  return { total_steps: params.totalSteps }
}

export interface OnboardingStepCompletedProperties {
  [key: string]: unknown
  step_number: number
  step_name: string
  time_spent_s?: number
}

export function buildOnboardingStepCompletedProperties(params: {
  stepNumber: number
  stepName: string
  timeSpentS?: number
}): OnboardingStepCompletedProperties {
  const props: OnboardingStepCompletedProperties = {
    step_number: params.stepNumber,
    step_name: params.stepName,
  }
  if (params.timeSpentS != null) props.time_spent_s = params.timeSpentS
  return props
}

export interface OnboardingCompletedProperties {
  [key: string]: unknown
  voice_type?: string
  experience_level?: string
  goal?: string
  duration_s?: number
}

export function buildOnboardingCompletedProperties(params: {
  voiceType?: string
  experienceLevel?: string
  goal?: string
  durationS?: number
}): OnboardingCompletedProperties {
  const props: OnboardingCompletedProperties = {}
  if (params.voiceType != null) props.voice_type = params.voiceType
  if (params.experienceLevel != null)
    props.experience_level = params.experienceLevel
  if (params.goal != null) props.goal = params.goal
  if (params.durationS != null) props.duration_s = params.durationS
  return props
}

// ---------------------------------------------------------------------------
// Paywall (P0 — new events)
// ---------------------------------------------------------------------------

export interface PaywallViewedProperties {
  [key: string]: unknown
  trigger: string
  plan_shown?: string
  discount_visible?: boolean
}

export function buildPaywallViewedProperties(params: {
  trigger: string
  planShown?: string
  discountVisible?: boolean
}): PaywallViewedProperties {
  const props: PaywallViewedProperties = { trigger: params.trigger }
  if (params.planShown != null) props.plan_shown = params.planShown
  if (params.discountVisible != null)
    props.discount_visible = params.discountVisible
  return props
}

export interface PaywallDismissedProperties {
  [key: string]: unknown
  time_on_screen_s?: number
  plan_shown?: string
}

export function buildPaywallDismissedProperties(params: {
  timeOnScreenS?: number
  planShown?: string
}): PaywallDismissedProperties {
  const props: PaywallDismissedProperties = {}
  if (params.timeOnScreenS != null)
    props.time_on_screen_s = params.timeOnScreenS
  if (params.planShown != null) props.plan_shown = params.planShown
  return props
}

// ---------------------------------------------------------------------------
// Subscription Lifecycle (P0 — new events)
// ---------------------------------------------------------------------------

export interface SubscriptionStartedProperties {
  [key: string]: unknown
  plan: string
  price: number
  currency: string
  billing_period: string
  discount_code?: string
}

export function buildSubscriptionStartedProperties(params: {
  plan: string
  price: number
  currency: string
  billingPeriod: string
  discountCode?: string
}): SubscriptionStartedProperties {
  const props: SubscriptionStartedProperties = {
    plan: params.plan,
    price: params.price,
    currency: params.currency,
    billing_period: params.billingPeriod,
  }
  if (params.discountCode != null) props.discount_code = params.discountCode
  return props
}

export interface SubscriptionCancelledProperties {
  [key: string]: unknown
  reason?: string
  tenure_days?: number
}

export function buildSubscriptionCancelledProperties(params: {
  reason?: string
  tenureDays?: number
}): SubscriptionCancelledProperties {
  const props: SubscriptionCancelledProperties = {}
  if (params.reason != null) props.reason = params.reason
  if (params.tenureDays != null) props.tenure_days = params.tenureDays
  return props
}

export interface PaymentFailedProperties {
  [key: string]: unknown
  reason?: string
  plan?: string
  attempt_number?: number
}

export function buildPaymentFailedProperties(params: {
  reason?: string
  plan?: string
  attemptNumber?: number
}): PaymentFailedProperties {
  const props: PaymentFailedProperties = {}
  if (params.reason != null) props.reason = params.reason
  if (params.plan != null) props.plan = params.plan
  if (params.attemptNumber != null) props.attempt_number = params.attemptNumber
  return props
}
